<template>
  <div class="max-w-md mx-auto text-center">
    <div class="mb-6">
      <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
        <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      </div>
    </div>

    <h2 class="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>

    <p class="text-gray-600 mb-6">We've sent a verification link to:</p>

    <div class="bg-gray-50 rounded-lg p-4 mb-6">
      <p class="font-medium text-gray-900">{{ email }}</p>
    </div>

    <p class="text-sm text-gray-600 mb-6">
      Click the link in the email to verify your account. If you don't see the email, check your
      spam folder.
    </p>

    <div class="space-y-4">
      <Button
        variant="primary"
        size="lg"
        :loading="isResending"
        :disabled="!canResend"
        full-width
        @click.stop.prevent="resendEmail"
      >
        {{ resendButtonText }}
      </Button>

      <Button variant="outline" size="lg" full-width @click="changeEmail">
        Use different email
      </Button>
    </div>

    <div class="mt-6 text-center">
      <p class="text-sm text-gray-600">
        Already verified?
        <router-link
          to="/auth/login"
          class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
        >
          Sign in
        </router-link>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue';
  import { Button } from '@asabdev/asab-ui';

  interface Props {
    email: string;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    resend: [email: string];
    changeEmail: [];
    error: [error: string];
  }>();

  // Removed requestVerifyToken and error since parent handles the API call

  // Resend functionality with persistence
  const isResending = ref(false);
  const cooldownInterval = ref<NodeJS.Timeout | null>(null);
  const lastResendTime = ref(0);

  // Use a unique key based on email for persistence
  const storageKey = `email_verification_${props.email}`;

  // Get persisted state or initialize
  const getInitialState = () => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const { cooldown, count, timestamp } = JSON.parse(stored);
        const elapsed = Math.floor((Date.now() - timestamp) / 1000);
        const remainingCooldown = Math.max(0, cooldown - elapsed);
        return { cooldown: remainingCooldown, count };
      }
    } catch {
      // Ignore storage errors
    }
    return { cooldown: 30, count: 0 }; // Initial 30s cooldown
  };

  const initialState = getInitialState();
  const resendCooldown = ref(initialState.cooldown);
  const resendCount = ref(initialState.count);

  const canResend = computed(() => resendCooldown.value === 0 && !isResending.value);

  const resendButtonText = computed(() => {
    if (isResending.value) {
      return 'Sending...';
    }
    if (resendCooldown.value > 0) {
      return `Resend in ${resendCooldown.value}s`;
    }
    return resendCount.value === 0 ? 'Resend verification email' : 'Resend again';
  });

  const saveState = () => {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          cooldown: resendCooldown.value,
          count: resendCount.value,
          timestamp: Date.now(),
        })
      );
    } catch {
      // Ignore storage errors
    }
  };

  const startCooldown = (customTime?: number) => {
    // Clear any existing interval
    if (cooldownInterval.value) {
      clearInterval(cooldownInterval.value);
    }

    // Use custom time or progressive cooldown: first = 30s, subsequent = 60s
    if (customTime !== undefined) {
      resendCooldown.value = customTime;
    } else if (resendCooldown.value <= 0) {
      // Only set new cooldown if current one is expired
      const cooldownTime = resendCount.value === 0 ? 30 : 60;
      resendCooldown.value = cooldownTime;
    }

    // Save initial state
    saveState();

    cooldownInterval.value = setInterval(() => {
      resendCooldown.value--;
      saveState(); // Save state on each tick

      if (resendCooldown.value <= 0) {
        clearInterval(cooldownInterval.value!);
        cooldownInterval.value = null;
        // Clear storage when cooldown ends
        try {
          sessionStorage.removeItem(storageKey);
        } catch {
          // Ignore storage errors
        }
      }
    }, 1000);
  };

  const resendEmail = () => {
    if (!canResend.value) {
      return;
    }

    // Add a guard to prevent multiple rapid clicks
    if (isResending.value) {
      return;
    }

    // Debounce mechanism - prevent multiple calls within 1 second
    const now = Date.now();
    if (now - lastResendTime.value < 1000) {
      return;
    }
    lastResendTime.value = now;

    isResending.value = true;

    try {
      // Only emit the event, let the parent handle the API call
      emit('resend', props.email);
      resendCount.value++;

      // Start new cooldown: 30s for first resend, 60s for subsequent
      const cooldownTime = resendCount.value === 1 ? 30 : 60;
      startCooldown(cooldownTime);
    } finally {
      // Add a small delay to show the loading state
      setTimeout(() => {
        isResending.value = false;
      }, 500);
    }
  };

  const changeEmail = () => {
    emit('changeEmail');
  };

  onMounted(() => {
    // Only start cooldown if we have time remaining or it's the first load
    if (resendCooldown.value > 0) {
      startCooldown();
    }
  });

  onUnmounted(() => {
    if (cooldownInterval.value) {
      clearInterval(cooldownInterval.value);
    }
    // Save final state before unmounting
    saveState();
  });
</script>
